import { motion } from "framer-motion";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getFiles } from "@/entities/FileService/File.queries";
import { AgGridReact } from "ag-grid-react";
import { useRef } from "react";
import { fileTableTheme } from "@/features/FileManagement/fileTableTheme";
import { FileSource } from "@/entities/FileService/File.model";
import { formatDate } from "@/utils/misc/format";
import clsx from "clsx";
import { CreatorCell } from "@/features/FileManagement/components/CreatedByCell";
import { SidebarPortal } from "@/features/Navigation/components/SidebarPortal";
import { PlusIcon } from "@heroicons/react/24/outline";
import { selectedUploadAtom, showUploadFormAtom } from "./uploads.state";
import { useAtom, useSetAtom } from "jotai";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";
import { RightDrawerContent } from "@/features/Navigation/components/RightDrawerContent";
import { NewUploadPanel } from "./NewUploadPanel";
import { UploadDetailPanel } from "./UploadDetailPanel";

export default function UploadsPage() {
    const { data: files } = useApiQuery(getFiles, {
        params: { source: FileSource.USER_UPLOADED },
    });
    const gridRef = useRef<AgGridReact>(null);
    const [selectedUpload, setSelectedUpload] = useAtom(selectedUploadAtom);
    const [showUploadForm, setShowUploadForm] = useAtom(showUploadFormAtom);
    const setRightDrawerOpen = useSetAtom(rightDrawerOpenAtom);
    if (!files) return null;

    return (
        <>
            <div>
                <SidebarPortal>
                    <div className="mt-6 px-3">
                        <h2 className="lozenge mb-4">Quick Actions</h2>
                        <ul className="flex flex-col gap-2">
                            <li>
                                <button
                                    type="button"
                                    className="quiet"
                                    onClick={() => {
                                        setShowUploadForm(true);
                                        setRightDrawerOpen(true);
                                        setSelectedUpload(null);
                                    }}
                                >
                                    <PlusIcon className="size-4" />
                                    Upload a new file
                                </button>
                            </li>
                        </ul>
                    </div>
                </SidebarPortal>

                {selectedUpload && (
                    <RightDrawerContent onClose={() => setSelectedUpload(null)}>
                        <UploadDetailPanel selectedUpload={selectedUpload} />
                    </RightDrawerContent>
                )}

                {showUploadForm && (
                    <RightDrawerContent
                        onClose={() => setShowUploadForm(false)}
                    >
                        <NewUploadPanel />
                    </RightDrawerContent>
                )}
            </div>
            <motion.div className="flex size-full">
                <div
                    className={clsx("size-full px-6", "flex flex-col", "gap-3")}
                >
                    <div id="labeling-grid" className="size-full">
                        <AgGridReact
                            ref={gridRef}
                            theme={fileTableTheme}
                            rowData={files.filter(
                                (file) =>
                                    file.source === FileSource.USER_UPLOADED,
                            )}
                            // getRowId={(params) => params.data.id}
                            // onCellClicked={onRowClicked}
                            columnDefs={[
                                {
                                    headerName: "Name",
                                    colId: "name",
                                    field: "name",
                                    pinned: "left" as const,
                                    suppressHeaderMenuButton: true,
                                    sortable: false,
                                    width: 600,

                                    // autoHeight: true,
                                    cellRenderer: (params) => {
                                        return (
                                            <div className="flex h-full items-center">
                                                <p className="body-s text-wrap">
                                                    {params.data?.name}
                                                </p>
                                            </div>
                                        );
                                    },
                                },
                                // {
                                //     headerName: "Content Type",
                                //     colId: "contentType",
                                //     suppressHeaderMenuButton: true,
                                //     sortable: false,
                                //     sort: "asc",

                                //     valueGetter: (params) => {
                                //         console.log(params.data);

                                //         if (!params.data) return "unknown";

                                //         const data = params.data;

                                //         return (
                                //             associatedFiles.find(
                                //                 (file) => file.id === data.id,
                                //             )?.documentKind ||
                                //             data.meta?.codatDataType ||
                                //             data.meta?.userReportedUploadType ||
                                //             "unknown"
                                //         );
                                //     },
                                //     cellRenderer: (params) => {
                                //         console.log(params);
                                //         return (
                                //             <ContentTypeCell
                                //                 type={params.value}
                                //             />
                                //         );
                                //     },
                                // },
                                {
                                    headerName: "Date created",
                                    colId: "createdAt",
                                    suppressHeaderMenuButton: true,
                                    sortable: false,
                                    valueGetter: (params) => {
                                        if (!params.data) return "";

                                        return formatDate(
                                            params.data.createdAt,
                                            {
                                                hideTime: true,
                                                dateFormat:
                                                    "YYYY-MM-DDTHH:mm:ssZ",
                                            },
                                        );
                                    },
                                },
                                {
                                    headerName: "Creator",
                                    colId: "creator",
                                    suppressHeaderMenuButton: true,
                                    sortable: false,
                                    cellRenderer: (params) => {
                                        if (!params.data) return null;

                                        return (
                                            <CreatorCell
                                                createdByType={
                                                    params.data.createdByType
                                                }
                                                createdByUserId={
                                                    params.data.createdByUserId
                                                }
                                            />
                                        );
                                    },
                                },
                            ]}
                            defaultColDef={{
                                flex: 1,
                            }}
                            rowSelection={{
                                mode: "multiRow",
                                checkboxes: false,
                                headerCheckbox: false,
                                enableClickSelection: true,
                            }}
                            onRowClicked={(params) => {
                                setSelectedUpload(params.data);
                                setRightDrawerOpen(true);
                                setShowUploadForm(false);
                            }}
                        />
                    </div>
                </div>
            </motion.div>
        </>
    );
}
